import React from 'react';
import { Images } from 'utils';
import { EditWithIcon, PhoneRow, StatusCard, TextRow } from 'components';

export const systemTypeHead = [
    { name: 'name', title: 'Name' },
    { name: 'displayCode', title: 'Display Code', custom: false },
    { name: 'category', title: 'Category', custom: false },
    { name: 'action', title: 'Action', custom: false, disabled: true, width: '100px' },
];

export const systemTypeBody = [
    { rowText: (item) => <TextRow name={item?.name} textWidth={10} /> },
    { rowText: (item) => <TextRow name={item?.displayCode} textWidth={10} /> },
    { rowText: (item) => <TextRow name={item?.category} textWidth={10} /> },
    {
        button: (cb) => (
            <div onClick={() => cb('edit')}>
                <img src={Images.edit} alt="edit" />
            </div>
        ),
        notClickable: true,
    },
];

export const ACTION_TYPE = 'GET_SERVICES';

export const systemNotYet = {
    title: 'No Service Types Added',
    subTitle: 'It looks like there are currently no service types listed. Use the inputs above to add new service types.',
    image: Images.notFile,
};

export const credentialTypes = [
    { name: 'Degree', id: 'DEGREE' },
    { name: 'Clearance', id: 'CLEARANCES' },
    { name: 'License', id: 'LICENSE' },
];

export const placeOfServiceHead = [
    { name: 'name', title: 'Name', custom: false },
    { name: 'code', title: 'Code', custom: false },
    { name: 'modifier', title: 'Modifier', custom: false },
    { name: '', title: 'Action', custom: false, disabled: true, width: '100px' },
];

export const placeOfServiceBody = [
    { rowText: (item) => <TextRow name={item?.name} /> },
    { rowText: (item) => <TextRow name={item?.code} /> },
    { rowText: (item) => <TextRow name={item?.modifier} /> },
    {
        button: (cb) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <EditWithIcon icon={Images.editAuth} type={'button'} handleClick={() => cb('edit')} />
                <EditWithIcon icon={Images.deleteTag} type={'button'} handleClick={() => cb('delete')} />
            </div>
        ),
        notClickable: true,
    },
];

export const PLACE_OF_ACTION_TYPE = 'GET_PLACES';

/**
 * CMS Place of Service code set (https://www.cms.gov/medicare/coding-billing/place-of-service-codes/code-sets).
 * Used only to prefill the Add/Edit Place of Service modal — every field stays editable, and the org's
 * own `place` documents remain the source of truth for billing.
 *
 * Names over the modal's 50-char limit are shortened (54, 66, 71). `modifier` is set only where one is
 * near-universal (telehealth → 95); payers vary (some want GT), so users can change it.
 */
export const STANDARD_PLACES_OF_SERVICE = [
    { code: '01', name: 'Pharmacy' },
    { code: '02', name: "Telehealth Provided Other than in Patient's Home", modifier: '95' },
    { code: '03', name: 'School' },
    { code: '04', name: 'Homeless Shelter' },
    { code: '05', name: 'Indian Health Service Free-standing Facility' },
    { code: '06', name: 'Indian Health Service Provider-based Facility' },
    { code: '07', name: 'Tribal 638 Free-standing Facility' },
    { code: '08', name: 'Tribal 638 Provider-based Facility' },
    { code: '09', name: 'Prison/Correctional Facility' },
    { code: '10', name: "Telehealth Provided in Patient's Home", modifier: '95' },
    { code: '11', name: 'Office' },
    { code: '12', name: 'Home' },
    { code: '13', name: 'Assisted Living Facility' },
    { code: '14', name: 'Group Home' },
    { code: '15', name: 'Mobile Unit' },
    { code: '16', name: 'Temporary Lodging' },
    { code: '17', name: 'Walk-in Retail Health Clinic' },
    { code: '18', name: 'Place of Employment-Worksite' },
    { code: '19', name: 'Off Campus-Outpatient Hospital' },
    { code: '20', name: 'Urgent Care Facility' },
    { code: '21', name: 'Inpatient Hospital' },
    { code: '22', name: 'On Campus-Outpatient Hospital' },
    { code: '23', name: 'Emergency Room - Hospital' },
    { code: '24', name: 'Ambulatory Surgical Center' },
    { code: '25', name: 'Birthing Center' },
    { code: '26', name: 'Military Treatment Facility' },
    { code: '27', name: 'Outreach Site/Street' },
    { code: '31', name: 'Skilled Nursing Facility' },
    { code: '32', name: 'Nursing Facility' },
    { code: '33', name: 'Custodial Care Facility' },
    { code: '34', name: 'Hospice' },
    { code: '41', name: 'Ambulance - Land' },
    { code: '42', name: 'Ambulance - Air or Water' },
    { code: '49', name: 'Independent Clinic' },
    { code: '50', name: 'Federally Qualified Health Center' },
    { code: '51', name: 'Inpatient Psychiatric Facility' },
    { code: '52', name: 'Psychiatric Facility-Partial Hospitalization' },
    { code: '53', name: 'Community Mental Health Center' },
    { code: '54', name: 'Intermediate Care Facility/IID' },
    { code: '55', name: 'Residential Substance Abuse Treatment Facility' },
    { code: '56', name: 'Psychiatric Residential Treatment Center' },
    { code: '57', name: 'Non-residential Substance Abuse Treatment Facility' },
    { code: '58', name: 'Non-residential Opioid Treatment Facility' },
    { code: '60', name: 'Mass Immunization Center' },
    { code: '61', name: 'Comprehensive Inpatient Rehabilitation Facility' },
    { code: '62', name: 'Comprehensive Outpatient Rehabilitation Facility' },
    { code: '65', name: 'End-Stage Renal Disease Treatment Facility' },
    { code: '66', name: 'PACE Center' },
    { code: '71', name: 'State or Local Public Health Clinic' },
    { code: '72', name: 'Rural Health Clinic' },
    { code: '81', name: 'Independent Laboratory' },
    { code: '99', name: 'Other Place of Service' },
].map((place) => ({ ...place, id: place.code }));

export const formatStandardPlace = (place) => `${place.code} - ${place.name}${place.modifier ? ` (${place.modifier})` : ''}`;

export const departNotYet = {
    title: 'No Departments Added',
    subTitle: 'Add departments to categorize your staff and manage your clinics more effectively.',
    image: Images.noFsYet,
};

export const jobTitleNotYet = {
    title: 'No Job Titles Added',
    subTitle: 'Add job titles to help classify and manage your staff roles.',
    image: Images.noRole,
};

export const placeOfNotYet = {
    title: 'No Places of Service Added',
    subTitle: 'Add locations to manage service types and categorize where services are provided.',
    image: Images.noFile,
};

export const credentialNotYet = {
    title: 'No Credentials Added',
    subTitle: 'Create credentials to track required qualifications and certifications for your staff.',
    image: Images.noCredential,
};

export const aiIntegrationEnums = {
    VOICE_NOTE: 'VOICE_NOTE',
};

export const clearHousingEnums = {
    ELIGIBILITY_CHECK: 'ELIGIBILITY_CHECK',
    CLAIM_STATUS: 'CLAIM_STATUS',
    PROFESSIONAL_CLAIM_SUBMISSION: 'PROFESSIONAL_CLAIM_SUBMISSION',
    CLAIM_PAYMENT: 'CLAIM_PAYMENT',
};

export const capabilitiesMapping = {
    ELIGIBILITY_CHECK: 'eligibilityCheck',
    CLAIM_STATUS: 'claimStatus',
    PROFESSIONAL_CLAIM_SUBMISSION: 'professionalClaimSubmission',
    CLAIM_PAYMENT: 'claimPayment',
};

export const clearHouseTypeEnums = {
    OFFICE_ALLY: 'OFFICE_ALLY',
    AVAILITY: 'AVAILITY',
    TL_CLEARINGHOUSE: 'TL_CLEARINGHOUSE',
};

// The names staff see — mirrors the backend CLEARINGHOUSE_DISPLAY_NAME (package clearinghouse.constants.ts).
export const clearHouseDisplayNames = {
    [clearHouseTypeEnums.TL_CLEARINGHOUSE]: 'TL Clearinghouse',
    [clearHouseTypeEnums.OFFICE_ALLY]: 'Office Ally',
    [clearHouseTypeEnums.AVAILITY]: 'Availity',
};

export const EDIT_INTEGRATION_ACTION_TYPE = 'EDIT_INTEGRATION_CONNECTION';
export const DISCONNECT_INTEGRATION_ACTION_TYPE = 'DISCONNECT_INTEGRATION';

export const taxTypeEnums = {
    SSN: 'SSN',
    EIN: 'EIN',
};
export const taxIdTypes = [
    {
        label: 'SSN',
        value: taxTypeEnums.SSN,
    },
    {
        label: 'EIN',
        value: taxTypeEnums.EIN,
    },
];

export const companyHead = [
    { name: 'name', title: 'Company Name', custom: false },
    { name: 'npi', title: 'NPI', custom: false },
    { name: 'taxId', title: 'Tax ID', custom: false },
    { name: '', title: 'Contact', custom: false },
    { name: '', title: 'Address', custom: false },
    { name: 'status', title: 'Status', custom: false, width: '140px' },
    { name: '', title: 'Action', custom: false, width: '100px' },
];

export const companyBody = [
    {
        rowText: (item) => (
            <div className="flex-align-center" style={{ gap: 8 }}>
                {/*{item?.isDefault ?*/}
                {/*    <img src={Images.officeBlueIcon} alt="officeBlueIcon" />*/}
                {/*    :*/}
                {/*    <img src={Images.officeMarkLocation} alt="officeBlueIcon" />*/}
                {/*}*/}
                <img src={Images.officeMarkLocation} alt="officeBlueIcon" />
                <TextRow name={item?.name} />

                {item?.isDefault && <div className="main-office-box">Main Office</div>}
            </div>
        ),
    },
    { rowText: (item) => <TextRow name={item?.npi} /> },
    { rowText: (item) => <TextRow name={item?.taxId} /> },
    {
        rowText: (item) => (
            <div>
                {item?.email && <TextRow name={item.email} />}
                {item?.phone && <PhoneRow phone={item.phone} />}
            </div>
        ),
    },
    {
        rowText: (item) => {
            if (!item?.address) return <TextRow name="-" />;
            const line1 = [item.address.street, item.address.unitNumber].filter(Boolean).join(', ');
            const stateZip = [item.address.state, item.address.zip].filter(Boolean).join(' ');
            const line2 = [item.address.city, stateZip, item.address.country].filter(Boolean).join(', ');
            return (
                <div>
                    {line1 && <TextRow name={line1} />}
                    {line2 && <TextRow name={line2} />}
                </div>
            );
        },
    },
    {
        rowText: (item) => <StatusCard status={item?.status} />,
    },
    {
        button: (cb) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <EditWithIcon icon={Images.editAuth} type={'button'} handleClick={() => cb('edit')} />
                <EditWithIcon icon={Images.deleteTag} type={'button'} handleClick={() => cb('delete')} />
            </div>
        ),
        notClickable: true,
    },
];

export const companyNotYet = {
    title: 'No Companies Added',
    subTitle: 'Add companies to manage your practice locations and their billing information.',
    image: Images.noFile,
};
